/**
 * Head sentinels for deterministic dev CSS order:
 *   [ …host link… | cui-start | unstyledButtonBase* | other CUI | cui-end | app | app-end ]
 *
 * Reconcile moves stray mf-publisher / CUI <style> nodes back into the correct zone
 * and keeps app-end as the last <head> child (host code often appendChild after it).
 */

const IS_CUI_STYLE = Symbol.for('@calendly/mf-publisher/cui-style')
const CUI_LAYER_START_ID = 'mf-publisher-cui-layer-start'
const CUI_LAYER_END_ID = 'mf-publisher-cui-layer-end'
const APP_LAYER_END_ID = 'mf-publisher-app-layer-end'
const APP_STYLE_ATTR = 'data-mf-publisher-app-style'

const HOST_CUI_STYLESHEET = /\/calendly-ui-(?:base|components|utils)-/i
const UNSTYLED_BUTTON_BASE = 'unstyledButtonBase'

/** Linaria hashes for Button/BareButton component sheets (not token globals). */
const COMPONENT_BUTTON_STYLE = /\.(?:base_|buttonCss|button_)[a-z0-9_-]+/i

/** @param {string} id @param {string} layer */
const createMarker = (id, layer) => {
  const marker = document.createElement('meta')
  marker.id = id
  marker.setAttribute('data-mf-publisher-style-layer', layer)
  return marker
}

/** @param {Element} node */
const isCuiStyleElement = (node) => (
  node.tagName === 'STYLE' && /** @type {HTMLStyleElement} */ (node)[IS_CUI_STYLE] === true
)

/** @param {Element} node */
const isHostCalendlyUiStylesheet = (node) => (
  node.tagName === 'LINK'
  && node.getAttribute('rel') === 'stylesheet'
  && HOST_CUI_STYLESHEET.test(node.getAttribute('href') || '')
)

/** @param {Element} node */
const isCuiLayerNode = (node) => isCuiStyleElement(node) || isHostCalendlyUiStylesheet(node)

/** @param {HTMLStyleElement} node */
const isUnstyledButtonBaseStyle = (node) => (
  node.tagName === 'STYLE' && (node.textContent || '').includes(UNSTYLED_BUTTON_BASE)
)

/** Host/shared @calendly/ui may inject bare-button without our insert hook. */
/** @param {HTMLStyleElement} node */
const isOrphanCuiStyle = (node) => {
  if (node.tagName !== 'STYLE') return false
  if (isCuiStyleElement(node)) return false

  return (node.textContent || '').includes(UNSTYLED_BUTTON_BASE)
}

/** @param {HTMLStyleElement} node */
const shouldRelocateToCuiZone = (node) => isCuiStyleElement(node) || isOrphanCuiStyle(node)

/** @param {Element} node @param {Element} anchor */
const isNodeAtOrAfter = (node, anchor) => (
  (node === anchor) || (anchor.compareDocumentPosition(node) === Node.DOCUMENT_POSITION_FOLLOWING)
)

/** @param {Element} node */
const isCuiLayerMarker = (node) => (
  node.id === CUI_LAYER_START_ID || node.id === CUI_LAYER_END_ID
)

/** @param {Element} node */
const isAppStyleLayerCandidate = (node) => {
  if (isCuiLayerNode(node)) return false
  if (node.id === APP_LAYER_END_ID || node.id === CUI_LAYER_END_ID || node.id === CUI_LAYER_START_ID) {
    return false
  }

  return node.tagName === 'STYLE'
    || (node.tagName === 'LINK' && node.getAttribute('rel') === 'stylesheet')
}

/**
 * @param {HTMLHeadElement} head
 * @param {Element | null} boundary
 */
const findLastCuiLayerBefore = (head, boundary) => {
  let last = null

  for (const node of head.children) {
    if (node === boundary) break
    if (!isCuiLayerMarker(node) && isCuiLayerNode(node)) {
      last = node
    }
  }

  return last
}

/**
 * @param {HTMLHeadElement} head
 * @param {Element} boundary
 */
const findFirstPublisherAppStyle = (head, boundary) => {
  for (const node of head.children) {
    if (node === boundary) break
    if (!isCuiLayerMarker(node) && node.getAttribute?.(APP_STYLE_ATTR) === 'true') {
      return node
    }
  }

  return null
}

/**
 * @param {HTMLHeadElement} head
 * @param {Element} boundary
 */
const findFirstAppStyleLayer = (head, boundary) => {
  for (const node of head.children) {
    if (node === boundary) break
    if (isAppStyleLayerCandidate(node)) {
      return node
    }
  }

  return null
}

/** @param {HTMLHeadElement} head */
const ensureAppLayerEnd = (head) => {
  let marker = document.getElementById(APP_LAYER_END_ID)

  if (!marker) {
    marker = createMarker(APP_LAYER_END_ID, 'app-end')
    head.appendChild(marker)
  }

  if (marker !== head.lastChild) {
    head.appendChild(marker)
  }

  return /** @type {HTMLMetaElement} */ (marker)
}

/**
 * @param {HTMLHeadElement} head
 * @param {Element} beforeNode
 */
const ensureCuiLayerEnd = (head, beforeNode) => {
  let marker = document.getElementById(CUI_LAYER_END_ID)

  if (!marker) {
    marker = createMarker(CUI_LAYER_END_ID, 'cui-end')
    head.insertBefore(marker, beforeNode)
    return /** @type {HTMLMetaElement} */ (marker)
  }

  if (isNodeAtOrAfter(marker, beforeNode)) {
    head.insertBefore(marker, beforeNode)
  }

  return /** @type {HTMLMetaElement} */ (marker)
}

/**
 * @param {HTMLHeadElement} head
 * @param {Element} cuiEnd
 */
const ensureCuiLayerStart = (head, cuiEnd) => {
  let marker = document.getElementById(CUI_LAYER_START_ID)

  if (!marker) {
    marker = createMarker(CUI_LAYER_START_ID, 'cui-start')
    head.insertBefore(marker, cuiEnd)
    return /** @type {HTMLMetaElement} */ (marker)
  }

  if (isNodeAtOrAfter(marker, cuiEnd)) {
    head.insertBefore(marker, cuiEnd)
  }

  return /** @type {HTMLMetaElement} */ (marker)
}

/**
 * bare-button reset must precede Button .base_* / .buttonCss so component styles win.
 *
 * @param {HTMLHeadElement} head
 * @param {Element} cuiEnd
 */
const findUnstyledInsertAnchor = (head, cuiEnd) => {
  let fallback = cuiEnd

  for (const node of head.children) {
    if (node === cuiEnd) break

    const isComponentCuiStyle = (
      !isCuiLayerMarker(node)
      && node.tagName === 'STYLE'
      && !isUnstyledButtonBaseStyle(/** @type {HTMLStyleElement} */ (node))
    )

    if (isComponentCuiStyle) {
      if (fallback === cuiEnd && isCuiStyleElement(node)) {
        fallback = node
      }

      if (COMPONENT_BUTTON_STYLE.test(node.textContent || '')) {
        return node
      }
    }
  }

  return fallback
}

/** @param {HTMLHeadElement} head @param {Element} cuiEnd @param {HTMLStyleElement} node */
const insertUnstyledIntoCuiZone = (head, cuiEnd, node) => {
  const anchor = findUnstyledInsertAnchor(head, cuiEnd)

  head.insertBefore(node, anchor)
  node[IS_CUI_STYLE] = true
}

/** @param {HTMLHeadElement} head @param {Element} cuiEnd @param {HTMLStyleElement} node */
const insertIntoCuiZone = (head, cuiEnd, node) => {
  if (isUnstyledButtonBaseStyle(node)) {
    insertUnstyledIntoCuiZone(head, cuiEnd, node)
    return
  }

  const lastCui = findLastCuiLayerBefore(head, cuiEnd)

  if (lastCui && lastCui !== node) {
    lastCui.insertAdjacentElement('afterend', node)
  } else {
    head.insertBefore(node, cuiEnd)
  }

  node[IS_CUI_STYLE] = true
}

/** @param {HTMLHeadElement} head @param {Element} cuiEnd */
const reorderUnstyledBeforeComponentCui = (head, cuiEnd) => {
  const anchor = findUnstyledInsertAnchor(head, cuiEnd)

  for (const node of head.querySelectorAll('style')) {
    if (
      isUnstyledButtonBaseStyle(node)
      && isNodeAtOrAfter(node, anchor)
    ) {
      head.insertBefore(node, anchor)
    }
  }
}

/**
 * Linaria hashes class names (e.g. .unstyledButtonBase_o1msrq9s). Do not collapse every
 * tag that mentions unstyledButtonBase — only drop byte-identical reinject/HMR copies.
 *
 * @param {HTMLStyleElement} keep
 */
const dedupeIdenticalStyleContent = (keep) => {
  const text = keep.textContent || ''

  if (!text) return

  for (const node of document.head.querySelectorAll('style')) {
    if (node !== keep && node.textContent === text) {
      node.remove()
    }
  }
}

/** @param {HTMLHeadElement} head @param {Element} cuiEnd */
const relocateCuiStylesAfterBoundary = (head, cuiEnd) => {
  for (const node of head.querySelectorAll('style')) {
    const style = /** @type {HTMLStyleElement} */ (node)

    if (shouldRelocateToCuiZone(style) && isNodeAtOrAfter(style, cuiEnd)) {
      insertIntoCuiZone(head, cuiEnd, style)
    }
  }
}

/** @param {HTMLHeadElement} head @param {Element} appEnd */
const relocateAppStylesAfterBoundary = (head, appEnd) => {
  for (const node of head.querySelectorAll(`style[${APP_STYLE_ATTR}]`)) {
    if (isNodeAtOrAfter(node, appEnd)) {
      head.insertBefore(node, appEnd)
    }
  }
}

const reconcilePublisherStyleLayers = () => {
  const { head } = document
  const appEnd = ensureAppLayerEnd(head)
  const firstApp = (
    findFirstPublisherAppStyle(head, appEnd)
    ?? findFirstAppStyleLayer(head, appEnd)
    ?? appEnd
  )
  const cuiEnd = ensureCuiLayerEnd(head, firstApp)

  ensureCuiLayerStart(head, cuiEnd)
  relocateCuiStylesAfterBoundary(head, cuiEnd)
  reorderUnstyledBeforeComponentCui(head, cuiEnd)
  relocateAppStylesAfterBoundary(head, appEnd)
  ensureAppLayerEnd(head)
}

let reconcileQueued = false

const scheduleReconcilePublisherStyleLayers = () => {
  if (reconcileQueued) return
  reconcileQueued = true

  queueMicrotask(() => {
    reconcileQueued = false
    reconcilePublisherStyleLayers()
  })
}

let observerStarted = false

const startStyleLayerObserver = () => {
  if (observerStarted || typeof MutationObserver === 'undefined') return

  observerStarted = true

  const observer = new MutationObserver(() => {
    scheduleReconcilePublisherStyleLayers()
  })

  observer.observe(document.head, { childList: true })
}

module.exports = {
  IS_CUI_STYLE,
  APP_LAYER_END_ID,
  CUI_LAYER_END_ID,
  CUI_LAYER_START_ID,
  APP_STYLE_ATTR,
  ensureAppLayerEnd,
  ensureCuiLayerEnd,
  findFirstPublisherAppStyle,
  findFirstAppStyleLayer,
  insertIntoCuiZone,
  dedupeIdenticalStyleContent,
  reconcilePublisherStyleLayers,
  scheduleReconcilePublisherStyleLayers,
  startStyleLayerObserver,
}
